import React, { useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import LogOutLink from '~/components/links/LogOutLink';
import UsernameWithAvatar from '~/components/links/UsernameWithAvatar';
import { useSelector } from '~/redux';

import { styled, dimensions, transitionDefault, colors, fluidRange, breakpoints, calcEm, respondFrom, css } from '~/styles';
import { Stylable } from '~/types';


const baseFontSize = 13;
const highlightColor = '#e6d5c8';
const StyledHamburgerMenu = styled.nav`
  position: fixed;
  top: ${dimensions.headerHeight.mobile}px;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: auto;
  background: rgba(0, 0, 0, 0.90);
    /* animate under the header */
  z-index: -1;
  ${fluidRange('fontSize', `${baseFontSize}px`, '18px', breakpoints.xs, dimensions.mobileMenuBreakpoint)};
  font-weight: normal;
  letter-spacing: 0.015em;
  padding-bottom: 2em;

  ${transitionDefault};
  transition-timing-function: ease-out;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-100%);

  ${respondFrom(breakpoints.md, css`
    top: ${dimensions.headerHeight.desktop}px;
  `)}

  &.opened {
    opacity: 1;
    visibility: visible;
    transform: none;
  }

  ul {
    display: block !important;
    position: relative !important;
    width: 100%;
  }

  li {
    display: block;
    position: relative;
    margin-top: -1px;

    /* normal separator */
    &:not(:first-of-type) {
      &:before {
        content: '';
        display: block;
        height: 1px;
        position: absolute;
        left: ${calcEm(25, baseFontSize)};
        right: ${calcEm(25, baseFontSize)};
        top: 0;
        background: ${colors.white};
        border-radius: 50%;
        z-index: 1;
      }
    }

    /* dotted separator */
    &.hm-separator {
      height: 1px;
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3cline x1='0' x2='100%25' y1='0' y2='0' fill='none' stroke='white' stroke-width='4' stroke-dasharray='2%2c 6' stroke-dashoffset='0' stroke-linecap='butt'/%3e%3c/svg%3e");
      &:before {
        height: 0;
        background: none;
      }

      & + li {
        margin-top: 0;
        &:before {
          display: none;
        }
      }
    }
  }

  a {
    line-height: ${calcEm(50, baseFontSize)};
    height: ${calcEm(50, baseFontSize)};
    padding: 0 ${calcEm(25, baseFontSize)};

    ${transitionDefault}
    transition-property: background-color, color;

    span {
      transform: none !important;
      ${transitionDefault};
    }

    &.active {
      background-color: ${highlightColor};
      color: ${colors.black};
    }

    &:hover, &:active {
      &:not(.active) {
        background-color: rgba(0, 0, 0, 1);

        span {
          transform: translateX(5px) !important;
        }
      }
    }
  }

  ul.submenu {
    padding: 0 0 1em;
    li {
      &:before {
        display: none;
      }
    }
    a {
      line-height: ${calcEm(30, baseFontSize)};
      height: ${calcEm(30, baseFontSize)};
      padding: 0 ${calcEm(40, baseFontSize)};

      &.active {
        background: none;
        color: ${highlightColor};
        font-weight: 700;
      }
    }
  }
`

const StyledNavLink = styled(NavLink)`

`

const StyledUsernameWithAvatar = styled(UsernameWithAvatar)`
  pointer-events: none;
  display: flex !important;
  align-items: center;
  height: ${calcEm(60, baseFontSize)} !important;

  img {
    margin-right: 10px;
    top: -2px;
  }
`

interface MenuItem {
  name: string;
  to: string;
  exact?: boolean;
  subitems?: MenuItem[];
}

const menuItems: MenuItem[] = [
  {
    name: 'Resources',
    to: '/resources',
    exact: true,
    subitems: [
      {
        name: 'All',
        to: '/resources/gallery'
      },
      {
        name: 'Favorites',
        to: '/resources/gallery?filters=Favorites'
      },
      {
        name: 'Documents',
        to: '/resources/gallery?filters=Document'
      },
      {
        name: 'Videos',
        to: '/resources/gallery?filters=Video'
      },
      {
        name: 'Links',
        to: '/resources/gallery?filters=Link'
      },
      // {
      //   name: 'Training',
      //   to: '/resources/gallery?filters=Training'
      // }
    ]
  },
  {
    name: 'Auditorium',
    to: '/auditorium'
  },
]

interface Props extends Stylable {
  isOpened: boolean;
  close: () => void;
}
const HamburgerMenu: React.FC<Props> = ({ className, isOpened, close }) => {
  const user = useSelector(state => state.user);
  const currentLocation = useLocation();

  const isSubitemActive = useCallback((item: MenuItem) => {
    return (item.to === currentLocation.pathname && !currentLocation.search) || (
      !!currentLocation.search && item.to.indexOf(currentLocation.search) !== -1 && item.to.endsWith(currentLocation.search)
    );
  }, [currentLocation])

  return (
    <StyledHamburgerMenu
      className={`
        ${isOpened ? 'opened' : ''}
        ${className}
      `} 
    >
      <ul>
        {
          menuItems.map( item => (
            <li key={item.to}>
              <StyledNavLink exact={item.exact} to={item.to}
                onClick={close}
              >
                <span dangerouslySetInnerHTML={{ __html: item.name }} />
              </StyledNavLink>

              {
                !!item.subitems && (
                  <ul className='submenu'>
                    {
                      item.subitems.map( subitem => (
                        <li key={subitem.to}>
                          <StyledNavLink exact={subitem.exact} to={subitem.to}
                            isActive={() => isSubitemActive(subitem)}
                            onClick={close}
                          >
                            <span dangerouslySetInnerHTML={{ __html: subitem.name }} />
                          </StyledNavLink>
                        </li>
                      ))
                    }
                  </ul>
                )

              }
            </li>
          ) )
        }

        { 
          !!user &&
          <>
            <li className='hm-separator' />
            <li>
              <StyledUsernameWithAvatar />
            </li>
            <li>
              <LogOutLink as='a' />
            </li>
          </>
        }
      </ul>
    </StyledHamburgerMenu>
  )
}

export default HamburgerMenu;


// WEBPACK FOOTER //
// ./src/components/layout/header/HamburgerMenu.tsx